import React, { useEffect, useState } from 'react';
import { ShieldCheck, Wallet, TrendingUp, Clock4 } from 'lucide-react';
import VideoAsset from '../assets/hidden-fees.webm';

const VideoSection: React.FC = () => {
  const defaultUTM =
  "?utm_source=online&utm_medium=website&utm_campaign=website_lead&utm_term=landing_page";

const [utmParams, setUtmParams] = useState<string>(() => {
  // Try to get from localStorage on initial load (e.g. second visit)
  return localStorage.getItem('utm_params') || defaultUTM;
});

useEffect(() => {
  const queryString = window.location.search;
  if (queryString && queryString.includes('utm_')) {
    localStorage.setItem('utm_params', queryString);
    setUtmParams(queryString); // Triggers rerender
  }
}, []);

const signupLink = `https://app.eximpe.com/auth/sign-up${utmParams}`;


  const features = [
    {
      icon: <ShieldCheck className="text-[#1c9e86] mb-2" size={24} />,
      title: "No hidden fees",
      description: "Complete transparency in all transactions"
    },
    {
      icon: <Wallet className="text-[#1c9e86] mb-2" size={24} />,
      title: "Low transaction fees",
      description: "Save on every international payment"
    },
    {
      icon: <TrendingUp className="text-[#1c9e86] mb-2" size={24} />,
      title: "Competitive rates",
      description: "Better exchange rates than traditional banks"
    },
    {
      icon: <Clock4 className="text-[#1c9e86] mb-2" size={24} />,
      title: "Fast processing",
      description: "Quick transfers within 24-48 hours"
    }
  ];

  return (
    <section className="py-20 bg-white relative lg:px-[13%] overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 right-0 h-1/2 bg-gray-50 -z-10"></div>

      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block px-4 py-1 rounded-full bg-[#ecf6f3] text-[#1c9e86] font-medium text-sm mb-4">
            Transparent Pricing
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
            No Hidden Charges
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            We believe in complete transparency. You'll never be surprised by hidden fees or unexpected charges.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center bg-white rounded-2xl shadow-xl mt-[-8%] overflow-hidden">
          {/* Left side - Video container */}
          <div className="w-full md:w-1/2 p-4 sm:p-8">
            <div className="relative justify-center pb-10 pt-10 items-center flex rounded-2xl overflow-hidden">
              <video
                src={VideoAsset}
                className="w-[60%] rounded-xl relative z-10"
                autoPlay
                muted
                loop
                playsInline
                style={{
                  display: 'block',
                  border: 'none',
                  margin: 0,
                  padding: 0
                }}
              />
            </div>
          </div>

          {/* Right side - Text content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-12">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">
              Transparent Pricing Model
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="p-4 rounded-lg border border-gray-100 bg-gray-50 hover:shadow-md transition-shadow">
                  {feature.icon}
                  <h4 className="text-base sm:text-lg font-semibold mb-2">{feature.title}</h4>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Call to action */}
            <a href={signupLink}>
              <button className="w-full hover:cursor-pointer bg-[#1c9e86] hover:bg-[#168a75] text-white font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg text-center">
                Sign Up Now
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
